import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoVacio } from '@/components/EstadoVacio';
import { Icono } from '@/components/Icono';
import {
  tokenDelHash, useEnviarFormularioPublico, useFormularioPublico,
  type CampoPublico,
} from './api';

/**
 * El formulario público de Recolección de Datos. Módulo 18, primer corte.
 *
 * Se abre con un enlace que lleva un token y **sin sesión**: lo rellena alguien
 * de fuera de la organización —un candidato, un proveedor— a quien Bienestar le
 * envió la invitación.
 *
 * ## El defecto que se llevó por delante
 *
 * El texto de ayuda de un campo se interpolaba **en crudo**:
 *
 * ```js
 * ${f.help ? `<div style="…">${f.help}</div>` : ''}
 * ```
 *
 * Todo lo que tenía al lado —la etiqueta, las opciones, los valores, la
 * descripción, la nota de devolución— pasaba por `Utils.escapeHtml`. Solo la
 * ayuda no. Y es el mismo tipo de dato: texto que escribe quien diseña el
 * formulario, guardado tal cual por el servidor (`"help": f.get("help") or ""`,
 * sin sanear) y pintado en una página **que no pide sesión**.
 *
 * Estaba sin explotar —los dos formularios con esa clave la tienen vacía,
 * comprobado en la base el 2026-09-28— y es lo de menos: la puerta estaba
 * abierta. `test_escapado.py` no lo veía porque busca por el NOMBRE de la
 * expresión —`*_name`, `*_label`— y `f.help` no se parece a un nombre de
 * persona. Es el punto ciego que la ficha del módulo 18 tenía escrito desde el
 * 2026-09-10, encontrado por el único método que lo encuentra: leer el archivo
 * entero.
 *
 * Aquí no hay escapado que recordar, porque no se arma marcado: el texto es
 * texto.
 *
 * ## Lo demás que cambia
 *
 * - **El botón de enviar no se puede pulsar dos veces.** Era un
 *   `onclick="APP.submitPublicForm()"` sin estado: dos clics seguidos mandaban
 *   dos veces, y el segundo recibía «Este formulario ya fue enviado».
 * - **Los campos obligatorios se ven antes de enviar.** El asterisco lo pintaba
 *   un `<span style="color: var(--red-text)">*</span>`, que es el color diciendo
 *   el dato; ahora lo dice `Field`, que además lo anuncia a un lector de
 *   pantalla.
 * - **Lo que se escribe no se pierde al fallar el envío.** El estado vive en el
 *   componente, no en el DOM que `innerHTML` reemplazaba entero.
 */
export function FormularioPublico() {
  const token = tokenDelHash();
  const consulta = useFormularioPublico(token);
  const enviar = useEnviarFormularioPublico(token);

  const [valores, setValores] = useState<Record<string, string>>({});
  const [listo, setListo] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const d = consulta.data;

  if (!listo && d) {
    const inicial: Record<string, string> = {};
    for (const f of d.form.fields) {
      const v = d.data?.[f.key];
      inicial[f.key] = v == null ? '' : String(v);
    }
    setValores(inicial);
    setListo(true);
  }

  /* ── Lo que puede salir mal antes de enseñar nada ──────────────────── */

  if (!token) {
    return (
      <Tarjeta titulo="Formulario">
        <EstadoVacio
          icono="bi-link-45deg"
          titulo="Falta el enlace"
          descripcion="Esta dirección no trae el código del formulario. Abre el enlace tal como te lo enviaron, sin recortarlo."
        />
      </Tarjeta>
    );
  }

  if (consulta.isPending) {
    return (
      <Tarjeta titulo="Formulario">
        <SkeletonFilas filas={5} columnas={1} />
      </Tarjeta>
    );
  }

  if (consulta.isError || !d) {
    // NO se usa `EstadoError` aquí, y es a propósito: sus mensajes hablan de rol
    // y de sesión —«esto no está en tu rol»— y quien abre este enlace está FUERA
    // de la organización y no tiene cuenta. Decirle que le faltan permisos sería
    // mentirle. Era así en el armazón y sigue siéndolo.
    return (
      <Tarjeta titulo="Formulario">
        <EstadoVacio
          icono="bi-link-45deg"
          titulo="Este enlace ya no sirve"
          descripcion="Puede que el formulario se haya cerrado, o que el enlace haya vencido. Escríbele a quien te lo envió y pídele uno nuevo."
        />
      </Tarjeta>
    );
  }

  const cabecera = {
    titulo: d.form.name,
    subtitulo: d.invited_name ? `Para: ${d.invited_name}` : undefined,
    icono: d.form.icon,
  };

  if (enviado) {
    return (
      <Tarjeta {...cabecera}>
        <p
          className="rounded-sm border border-ok bg-ok/10 p-5 text-center text-3 text-ok-ink"
          role="status"
        >
          <i className="bi bi-check-circle text-7" aria-hidden="true" />
          <strong className="mt-2 block">¡Formulario enviado!</strong>
          El área de Bienestar revisará la información. Ya puedes cerrar esta ventana.
        </p>
      </Tarjeta>
    );
  }

  if (!d.editable) {
    return (
      <Tarjeta {...cabecera}>
        <p className="rounded-sm border border-ok bg-ok/10 p-4 text-3 text-ok-ink">
          <i className="bi bi-check-circle" aria-hidden="true" /> Este formulario ya fue enviado
          y está en estado <strong>{d.status}</strong>. ¡Gracias!
        </p>
      </Tarjeta>
    );
  }

  /* ── El formulario ─────────────────────────────────────────────────── */

  const faltan = d.form.fields
    .filter((f) => f.required && !valores[f.key]?.trim())
    .map((f) => f.label);

  return (
    <Tarjeta {...cabecera}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          enviar.mutate(valores, {
            onSuccess: () => {
              // Enviado con éxito: ya no hay nada que proteger. Sin esto, quien
              // acaba de enviar recibe el aviso de «datos sin guardar» al cerrar
              // la pestaña —sobre un formulario que SÍ se guardó—. Lo marca el
              // listener que `app.js` cuelga del contenedor, y solo el envío
              // sabe cuándo deja de ser verdad.
              window._appFormDirty = false;
              setEnviado(true);
            },
          });
        }}
      >
        {d.form.description && <p className="text-3 text-muted">{d.form.description}</p>}

        {d.status === 'devuelta' && d.review_note && (
          <p className="rounded-sm border border-warn bg-warn/10 p-3 text-3 text-warn-ink">
            <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />{' '}
            <strong>Devuelto para corrección:</strong> {d.review_note}
          </p>
        )}

        {d.form.fields.map((f) => (
          <CampoDelFormulario
            key={f.key}
            campo={f}
            valor={valores[f.key] ?? ''}
            onCambio={(v) => setValores((previos) => ({ ...previos, [f.key]: v }))}
          />
        ))}

        {enviar.isError && (
          <p className="rounded-sm border border-danger bg-danger/8 p-3 text-3 text-danger-ink" role="alert">
            {(enviar.error as Error).message}
          </p>
        )}

        <Button
          type="submit"
          block
          loading={enviar.isPending}
          disabled={faltan.length > 0}
          razonDeshabilitado={`Falta por llenar: ${faltan.join(', ')}`}
        >
          <i className="bi bi-send" aria-hidden="true" /> Enviar formulario
        </Button>

        <p className="text-center text-1 text-muted">
          La información será revisada por el área de Bienestar. Uso interno · datos tratados
          según la política de la compañía.
        </p>
      </form>
    </Tarjeta>
  );
}

/** Un campo, según su tipo. El texto de ayuda va por `ayuda` de `Field`, que lo
 *  ata al control con `aria-describedby` — antes era un `<div>` suelto debajo,
 *  que un lector de pantalla no relaciona con nada. */
function CampoDelFormulario({ campo: f, valor, onCambio }: {
  campo: CampoPublico;
  valor: string;
  onCambio: (v: string) => void;
}) {
  const comun = {
    value: valor,
    onChange: (e: { target: { value: string } }) => onCambio(e.target.value),
  };

  return (
    <Field label={f.label} ayuda={f.help || undefined} opcional={!f.required}>
      {f.type === 'textarea' ? (
        <Textarea rows={3} {...comun} />
      ) : f.type === 'select' ? (
        <Select {...comun}>
          <option value="">—</option>
          {(f.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </Select>
      ) : (
        <Input
          type={f.type === 'number' ? 'number'
            : f.type === 'date' ? 'date'
              : f.type === 'email' ? 'email' : 'text'}
          {...comun}
        />
      )}
    </Field>
  );
}

/** La tarjeta con su cabecera. Vivía en `index.html` con su icono y su título
 *  puestos por `getElementById`; aquí es del componente, que es quien sabe qué
 *  formulario está enseñando. */
function Tarjeta({ titulo, subtitulo, icono, children }: {
  titulo: string;
  subtitulo?: string;
  icono?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-xl rounded-md border border-line bg-surface p-6 shadow-raised">
      <header className="mb-5 flex items-center gap-3">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-sm bg-brand text-5 text-on-brand">
          <Icono nombre={icono} respaldo="bi-file-earmark-text" />
        </span>
        <div className="min-w-0">
          <h1 className="text-5 font-bold text-brand-ink">{titulo}</h1>
          {subtitulo && <p className="text-3 text-muted">{subtitulo}</p>}
        </div>
      </header>
      {children}
    </div>
  );
}
